import type { components, PaigeClient } from '@paige/api-client';
import type { AccountDetail, AccountNote, CustomerVolume, VolumeBreakdown } from './types';
import { unwrap } from './http';

/** Paige Mobile's sign-ins that belong to no company (docs/engineering/mobile-capture.md, 4.6). */
export type MobileLeads = components['schemas']['MobileLeads'];

/** What to read of a company's volume: days for the series, the heatmap's days, months for the ramp. */
export interface VolumeWindow {
  days: number;
  hourDays: number;
  months: number;
}

/**
 * The customer insights reads and writes (docs/engineering/admin-customer-insights.md): one company's
 * account, volume and breakdown, staff notes, and a staff credit change through the same
 * ledger Phone Home reads.
 */
export function insightsApi(client: PaigeClient) {
  return {
    async getAccount(tenantId: string): Promise<AccountDetail> {
      const r = await client.GET('/admin/tenants/{tenantId}/account', {
        params: { path: { tenantId } },
      });
      return unwrap(r) as AccountDetail;
    },
    async getCustomerVolume(tenantId: string, w: VolumeWindow): Promise<CustomerVolume> {
      const r = await client.GET('/admin/tenants/{tenantId}/volume', {
        params: {
          path: { tenantId },
          query: { days: w.days, hourDays: w.hourDays, months: w.months },
        },
      });
      return unwrap(r) as CustomerVolume;
    },
    async getVolumeBreakdown(tenantId: string, from: string, to: string): Promise<VolumeBreakdown> {
      const r = await client.GET('/admin/tenants/{tenantId}/volume/breakdown', {
        params: { path: { tenantId }, query: { from, to } },
      });
      return unwrap(r) as VolumeBreakdown;
    },
    ...notesApi(client),
    ...supportApi(client),
    getMobileLeads: async (): Promise<MobileLeads> =>
      unwrap(await client.GET('/admin/mobile-leads')) as MobileLeads,
    async deleteMobileLead(email: string): Promise<void> {
      unwrap(await client.DELETE('/admin/mobile-leads/{email}', { params: { path: { email } } }));
    },
  };
}

function notesApi(client: PaigeClient) {
  return {
    async listNotes(tenantId: string): Promise<AccountNote[]> {
      const r = await client.GET('/admin/tenants/{tenantId}/notes', {
        params: { path: { tenantId } },
      });
      return unwrap(r) as AccountNote[];
    },
    async addNote(
      tenantId: string,
      note: { kind: AccountNote['kind']; body: string; dueOn: string | null },
    ): Promise<AccountNote> {
      const r = await client.POST('/admin/tenants/{tenantId}/notes', {
        params: { path: { tenantId } },
        body: note,
      });
      return unwrap(r) as AccountNote;
    },
    async doneNote(tenantId: string, noteId: string): Promise<void> {
      const r = await client.POST('/admin/tenants/{tenantId}/notes/{noteId}/done', {
        params: { path: { tenantId, noteId } },
      });
      unwrap(r);
    },
    async changeCredits(tenantId: string, add: number, reason: string): Promise<void> {
      const r = await client.POST('/admin/tenants/{tenantId}/credits', {
        params: { path: { tenantId }, header: { 'Idempotency-Key': '' } },
        body: { add, reason },
      });
      unwrap(r);
    },
  };
}

/** Round 2: the owner and support's retry (AdminOwnerEndpoints, AdminSupportFixEndpoints). */
function supportApi(client: PaigeClient) {
  return {
    async setOwner(tenantId: string, owner: string | null): Promise<void> {
      const r = await client.PUT('/admin/tenants/{tenantId}/owner', {
        params: { path: { tenantId } },
        body: { owner },
      });
      unwrap(r);
    },
    async retryParked(tenantId: string): Promise<number> {
      const r = await client.POST('/admin/tenants/{tenantId}/retry-parked', {
        params: { path: { tenantId } },
        body: {},
      });
      return (unwrap(r) as { retried: number }).retried;
    },
  };
}

export type InsightsApi = ReturnType<typeof insightsApi>;

/** The staff roles that may download a device's logs (AdminDeviceLogEndpoints.Roles). */
export const DEVICE_LOG_ROLES: readonly string[] = ['support', 'engineering'];

/**
 * Where a device's log upload downloads (`GET /v1/admin/tenants/{tenantId}/devices/{deviceId}/logs/{logId}`,
 * a zip; the API audits each download). A plain link: the session cookie goes with it on the app's own origin.
 */
export const deviceLogUrl = (tenantId: string, deviceId: string, logId: string): string =>
  `/v1/admin/tenants/${encodeURIComponent(tenantId)}/devices/${encodeURIComponent(deviceId)}/logs/${encodeURIComponent(logId)}`;
